import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, RefObject } from "react";

/**
 * Hook to observe and return an element's height
 * @param elementRef React ref object pointing to the target element
 * @returns The current height of the element in pixels
 */
export const useElementHeight = elementRef => {
  const $ = _c(3);
  const [height, setHeight] = useState(0);
  let t0;
  let t1;
  if ($[0] !== elementRef) {
    t0 = () => {
      const element = elementRef.current;
      if (!element) {
        return;
      }
      setHeight(element.clientHeight);
      const resizeObserver = new ResizeObserver(entries => {
        const newHeight = Math.round(entries[0].contentRect.height);
        setHeight(newHeight);
      });
      resizeObserver.observe(element);
      return () => {
        resizeObserver.disconnect();
      };
    };
    t1 = [elementRef];
    $[0] = elementRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return height;
};

/**
 * Hook to observe and return heights for multiple elements
 * @param elementRefs Array of React ref objects pointing to target elements
 * @returns Array of current heights for each element in pixels
 */
export const useMultipleElementHeights = elementRefs => {
  const $ = _c(5);
  let t0;
  if ($[0] !== elementRefs.length) {
    t0 = () => Array.from({
      length: elementRefs.length
    }, _temp);
    $[0] = elementRefs.length;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [heights, setHeights] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== elementRefs) {
    t1 = () => {
      if (!elementRefs.length) {
        return;
      }
      const observers = [];
      const measureInitialHeights = () => {
        const measured = elementRefs.map(_temp2);
        setHeights(prevHeights => {
          const newHeights = [...prevHeights];
          let hasChanges = false;
          measured.forEach((height, index) => {
            if (height > 0 && newHeights[index] !== height) {
              newHeights[index] = height;
              hasChanges = true;
            }
          });
          return hasChanges ? newHeights : prevHeights;
        });
      };
      measureInitialHeights();
      const initialTimerId = setTimeout(measureInitialHeights, 50);
      elementRefs.forEach((ref_0, index_0) => {
        const element_0 = ref_0.current;
        if (!element_0) {
          return;
        }
        const resizeObserver = new ResizeObserver(entries => {
          const newHeight = Math.round(entries[0].contentRect.height);
          if (newHeight > 0) {
            setHeights(prevHeights_0 => {
              if (prevHeights_0[index_0] === newHeight) {
                return prevHeights_0;
              }
              const newHeights_0 = [...prevHeights_0];
              newHeights_0[index_0] = newHeight;
              return newHeights_0;
            });
          }
        });
        resizeObserver.observe(element_0);
        observers.push(resizeObserver);
      });
      return () => {
        clearTimeout(initialTimerId);
        observers.forEach(_temp3);
      };
    };
    t2 = [elementRefs];
    $[2] = elementRefs;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (heights.length !== elementRefs.length) {
    const newHeights_1 = Array.from({
      length: elementRefs.length
    }, _temp4);
    heights.forEach((height_0, i) => {
      if (i < elementRefs.length) {
        newHeights_1[i] = height_0;
      }
    });
    setHeights(newHeights_1);
  }
  return heights;
};
function _temp() {
  return 0;
}
function _temp2(ref) {
  const element = ref.current;
  return element ? Math.round(element.getBoundingClientRect().height) : 0;
}
function _temp3(observer) {
  return observer.disconnect();
}
function _temp4() {
  return 0;
}